/**
 * Geographic access rules for fulldetailpro.com.
 *
 *   Ventura County, CA      → allow
 *   Los Angeles County, CA  → allow
 *   Anywhere in Oregon      → allow (website-manager exception)
 *   Everything else         → block
 *
 * The IP lookup only returns state + approximate coordinates, so county
 * membership is decided by testing the coordinates against a simplified
 * outline of Ventura + Los Angeles counties. IP geolocation is city-level at
 * best, so this is an approximation near county lines.
 */

export type GeoDecision = 'allowed' | 'blocked' | 'unknown';

type Point = [lon: number, lat: number];

/** Simplified combined outline of mainland Ventura + Los Angeles counties (lon, lat). */
const VENTURA_LA_OUTLINE: Point[] = [
// Ventura / Santa Barbara line (Rincon → north)
[-119.48, 34.37], [-119.44, 34.9],
// Ventura & LA / Kern line (Frazier Park & Lebec stay outside, Gorman inside)
[-118.98, 34.8], [-118.6, 34.82], [-117.65, 34.82],
// LA / San Bernardino line (Claremont & Pomona inside, Montclair & Chino Hills outside)
[-117.66, 34.16], [-117.71, 34.08], [-117.76, 33.97],
// LA / Orange line (La Habra Heights inside, La Habra outside) to Seal Beach
[-117.95, 33.945], [-118.03, 33.87], [-118.1, 33.75],
// Coastline, padded offshore: Long Beach → Palos Verdes → Malibu → Oxnard → Ventura
[-118.25, 33.6], [-118.5, 33.65], [-118.6, 33.95], [-118.8, 33.95], [-119.1, 34.05], [-119.35, 34.2], [-119.55, 34.35]];

/** Santa Catalina & San Clemente islands are part of Los Angeles County. */
const LA_ISLANDS: Array<{ minLon: number; maxLon: number; minLat: number; maxLat: number }> = [
{ minLon: -118.65, maxLon: -118.28, minLat: 33.28, maxLat: 33.5 },
{ minLon: -118.62, maxLon: -118.3, minLat: 32.78, maxLat: 33.05 }];

function pointInPolygon([lon, lat]: Point, polygon: Point[]): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [xi, yi] = polygon[i];
    const [xj, yj] = polygon[j];
    const crosses = yi > lat !== yj > lat && lon < (xj - xi) * (lat - yi) / (yj - yi) + xi;
    if (crosses) inside = !inside;
  }
  return inside;
}

export function isInVenturaOrLaCounty(lat: number, lon: number): boolean {
  if (pointInPolygon([lon, lat], VENTURA_LA_OUTLINE)) return true;
  return LA_ISLANDS.some(b => lon >= b.minLon && lon <= b.maxLon && lat >= b.minLat && lat <= b.maxLat);
}

export interface GeoInput {
  country?: string;
  region?: string;
  lat?: number;
  lon?: number;
}

/** Applies the access rules. Returns 'unknown' when location can't be determined. */
export function decideAccess({ country, region, lat, lon }: GeoInput): GeoDecision {
  if (!country) return 'unknown';
  if (country !== 'US') return 'blocked';
  const state = region?.trim().toLowerCase();
  if (!state) return 'unknown';
  if (state === 'oregon') return 'allowed';
  if (state !== 'california') return 'blocked';
  // California: only Ventura + Los Angeles counties.
  if (lat === undefined || lon === undefined || Number.isNaN(lat) || Number.isNaN(lon)) return 'unknown';
  return isInVenturaOrLaCounty(lat, lon) ? 'allowed' : 'blocked';
}
